// frontend/src/services/api.js
import axios from 'axios';

export const API_BASE_URL = () => {
  // If running on localhost (development)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:7836/api';
  }

  // For production, use relative path (same domain)
  return '/api';
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Abgelaufene/ungültige Anmeldung: Sitzung beenden und zum Login
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('username');
      localStorage.removeItem('user');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) =>
    axios.post(`${API_BASE_URL()}/auth/login`, { username, password }),

  logout: () =>
    apiClient.post('/auth/logout'),

  getCurrentUser: () =>
    apiClient.get('/auth/me'),
};

// Data API
export const dataAPI = {
  getEbkpCodes: () =>
    apiClient.get('/ebkp-codes'),

  getCantons: () =>
    apiClient.get('/cantons'),

  getBuildingTypes: () =>
    apiClient.get('/building-types'),

  search: (searchRequest) =>
    apiClient.post('/search', searchRequest),

  /** Frontend settings from the backend .env: {estimationSearchResultLimit} */
  getConfig: () =>
    apiClient.get('/config'),

  /** Complete eBKP-H catalogue with mapped BKP codes: {codes: [{code, label, bkp}]} */
  getCodeCatalog: () =>
    apiClient.get('/codes/catalog'),

  /** ZIW inflation index bases: {bases: [{basis, label, latest_year, factors}]} */
  getInflationBases: () =>
    apiClient.get('/inflation/bases'),
};

// Cost Estimation API
export const estimationAPI = {
  /**
   * Save a new estimation or update an existing one
   * @param {Object} data - Estimation data
   * @param {string|null} data.name - Name for new estimation (required for new, null for update)
   * @param {number|null} data.estimation_id - ID for updating existing estimation
   * @param {string} data.username - Username
   * @param {Object} data.search_filters - Original search filters
   * @param {Object} data.project_inputs - Project inputs (GF, GV, etc.)
   * @param {Object} data.totals - Totals (grandTotal, perGF, perGV)
   * @param {string|null} data.master_project_id - Master project ID
   * @param {Array} data.rows - Array of row data with ebkp_code, row_data, is_bookmarked
   */
  save: (data) =>
    apiClient.post('/estimations/save', data),

  /**
   * Get list of all saved estimations (all users)
   */
  list: () =>
    apiClient.get('/estimations/list'),

  /**
   * Get a specific estimation by ID
   * @param {number} estimationId - Estimation ID
   */
  get: (estimationId) =>
    apiClient.get(`/estimations/${estimationId}`),

  /**
   * Delete an estimation
   * @param {number} estimationId - Estimation ID
   */
  delete: (estimationId) =>
    apiClient.delete(`/estimations/${estimationId}`),

  /**
   * Parse a SIA 416 Flächen workbook into per-sheet Mengen metrics
   * @param {File} file - The .xlsx file
   * @returns {sheets: [{name, metrics: [{abbrev, unit, label, value}]}]}
   */
  parseMengen: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/estimations/parse-mengen', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Administration API (Benutzerverwaltung, nur für Administratoren)
export const adminAPI = {
  /** Alle Rollen mit deutschen Bezeichnungen und Berechtigungen */
  getRoles: () =>
    apiClient.get('/admin/roles'),

  /** Alle Benutzerkonten */
  listUsers: () =>
    apiClient.get('/admin/users'),

  /** Neues Benutzerkonto anlegen: {username, email, password, full_name, role} */
  createUser: (data) =>
    apiClient.post('/admin/users', data),

  /** Rolle, Aktiv-Status, E-Mail, Name oder Passwort ändern */
  updateUser: (username, data) =>
    apiClient.patch(`/admin/users/${encodeURIComponent(username)}`, data),

  /** Benutzerkonto löschen */
  deleteUser: (username) =>
    apiClient.delete(`/admin/users/${encodeURIComponent(username)}`),
};

// Datenimport (Projekt-Upload, nur für Administratoren)
export const ingestAPI = {
  /** Erreichbarkeit der benötigten Dienste, erlaubte Hauptgruppen, Worker-Status */
  status: () =>
    apiClient.get('/ingest/status'),

  /**
   * Projektordner hochladen. `paths[i]` ist der relative Pfad von `files[i]`
   * im Ablage-Schema (<Hauptgruppe>/<Projektordner>/...). Antwort: {job_id, job, ...}
   */
  upload: (files, paths, overwrite, onUploadProgress) => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files', file, paths[index].split('/').pop());
    });
    formData.append('paths', JSON.stringify(paths));
    formData.append('overwrite', overwrite ? 'true' : 'false');
    return apiClient.post('/ingest/uploads', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 0,
    });
  },

  /** Letzte Uploads: {jobs: [...]} */
  listJobs: (limit = 20) =>
    apiClient.get('/ingest/jobs', { params: { limit } }),

  /** Ein Upload mit seinen Projekt-Ergebnissen */
  getJob: (jobId) =>
    apiClient.get(`/ingest/jobs/${encodeURIComponent(jobId)}`),
};

// Agent Chat API (streaming tool-use agent)
export const agentAPI = {
  /**
   * Stream a chat turn from the agent via SSE.
   * Uses fetch + ReadableStream because EventSource cannot send the
   * Authorization header and axios does not stream response bodies.
   *
   * @param {Object} opts
   * @param {string|null} opts.conversationId - Existing conversation or null
   * @param {string} opts.message - User message
   * @param {function} opts.onEvent - Called with each parsed SSE event object
   * @param {AbortSignal} [opts.signal] - Optional abort signal
   */
  streamChat: async ({ conversationId, message, onEvent, signal }) => {
    const response = await fetch(`${API_BASE_URL()}/agent/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({ conversation_id: conversationId, message }),
      signal,
    });

    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const data = await response.json();
        detail = data.detail || detail;
      } catch (e) { /* keep status text */ }
      throw new Error(detail);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let separatorIndex;
      while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        for (const line of rawEvent.split('\n')) {
          if (line.startsWith('data: ')) {
            onEvent(JSON.parse(line.slice(6)));
          }
        }
      }
    }
  },

  /** Chat history for the sidebar: [{id, title, updated_at}] */
  listConversations: () =>
    apiClient.get('/agent/conversations'),

  /** Load a persisted chat for re-rendering: {conversation_id, title, messages} */
  getConversation: (conversationId) =>
    apiClient.get(`/agent/conversations/${conversationId}`),

  renameConversation: (conversationId, title) =>
    apiClient.patch(`/agent/conversations/${conversationId}`, { title }),

  deleteConversation: (conversationId) =>
    apiClient.delete(`/agent/conversations/${conversationId}`),
};

export default {
  auth: authAPI,
  data: dataAPI,
  estimation: estimationAPI,
  agent: agentAPI,
  admin: adminAPI,
  ingest: ingestAPI,
};